// @ts-nocheck -- inherited from lift-teams; pre-existing type issues outside this port's scope
import { useState, useEffect } from 'react';

import { AlertFormData, Alert, BrandConfig } from '../types';
import { dummyCommunicationMethods } from '../utils/dummyData';

interface UseAlertFormDataProps {
  alert?: Alert | null;
  isDuplicating?: boolean;
  userEmail?: string;
  userIsTeam?: boolean;
}

export const useAlertFormData = ({
  alert,
  isDuplicating,
  userEmail,
  userIsTeam,
}: UseAlertFormDataProps) => {
  // Filter communication methods: only include Slack if user is_team is true
  const filteredCommunicationMethods = userIsTeam
    ? [...dummyCommunicationMethods]
    : dummyCommunicationMethods.filter(method => method.type !== 'Slack');

  const [formData, setFormData] = useState<AlertFormData>({
    alertName: '',
    description: '',
    emailId: userEmail || '',
    toolName: '',
    brandConfigs: [],
    communicationMethods: filteredCommunicationMethods,
    selectedMembers: [],
    slackRecipients: [],
    emailRecipients: [],
    emailCcRecipients: [],
    liftRecipients: [],
    liftNotificationUserRecipients: [],
    messageTemplate: '',
    alertMessage: '',
    notificationChannel: 'Slack',
    cadence: 'realtime',
    scheduleConfig: {
      time: '09:00',
      timezone: 'America/New_York',
      days: [1, 2, 3, 4, 5],
      day: 1,
    },
    messageTemplates: {
      slack: '',
      email: '',
      web: '',
    },
  });

  const [selectedBrands, setSelectedBrands] = useState<BrandConfig[]>([]);

  useEffect(() => {
    // If editing existing alert, populate form
    if (alert) {
      // Prefill message templates per channel if available
      const templates = alert.messageTemplates || {
        slack: alert.alertMessage || '',
        email: alert.alertMessage || '',
        web: alert.alertMessage || '',
      };

      // Derive recipients for Slack as JSON values used by MultiSelectTag
      // Prefer the pre-mapped slackRecipients if available
      const slackRecipients =
        alert.slackRecipients ||
        (alert.recipients?.slack || []).map(r => {
          // Determine if it's a user or channel based on recipient_type
          const type = r.recipient_type === 'slack_channel' ? 'channel' : 'user';
          // recipient_value can be either a string (user/channel name) or an object
          const name =
            typeof r.recipient_value === 'string' ? r.recipient_value : r.recipient_id || '';

          return JSON.stringify({
            type,
            id: r.recipient_id,
            name,
          });
        });
      // Derive recipients for Email as JSON values used by MultiSelectTag
      const emailRecipients = (alert.recipients?.email || []).map(r => {
        // Handle both old format (string email) and new format (object with name/email)
        const recipientValue = r.recipient_value;
        const email =
          typeof recipientValue === 'string' ? recipientValue : recipientValue?.email || '';
        const name =
          typeof recipientValue === 'object' && recipientValue?.name ? recipientValue.name : 'User';

        return JSON.stringify({
          id: r.recipient_id,
          name,
          email,
        });
      });

      // Derive CC email recipients (same format as email: id, name, email)
      const emailCcRecipients = (alert.recipients?.email_cc || []).map(r => {
        const recipientValue = r.recipient_value;
        const email =
          typeof recipientValue === 'string' ? recipientValue : recipientValue?.email || '';
        const name =
          typeof recipientValue === 'object' && recipientValue?.name ? recipientValue.name : 'User';

        return JSON.stringify({
          id: r.recipient_id,
          name,
          email,
        });
      });

      // Derive recipients for LIFT Notification Brand (web_brand type) as JSON values
      const liftRecipients = (alert.recipients?.web || [])
        .filter((r: any) => (r as any).recipient_type === 'web_brand')
        .map((r: any) => {
          const brandId = (r as any).brand_id || (r as any).recipient_id;
          const brandName = (r as any).brand_name || 'Brand';
          return JSON.stringify({
            id: brandId,
            name: brandName,
          });
        })
        // Remove duplicates based on brand_id
        .filter((value, index, self) => {
          try {
            const parsed = JSON.parse(value);
            return (
              index ===
              self.findIndex(v => {
                try {
                  const vParsed = JSON.parse(v);
                  return vParsed.id === parsed.id;
                } catch {
                  return false;
                }
              })
            );
          } catch {
            return true;
          }
        });

      // Derive recipients for LIFT Notification User (web_user type) as JSON values
      // Use recipient_value.user_id as the primary ID (as per user requirement)
      const liftNotificationUserRecipients = (alert.recipients?.web || [])
        .filter((r: any) => (r as any).recipient_type === 'web_user')
        .map((r: any) => {
          const value = (r as any).recipient_value as { name?: string; user_id?: number } | null;
          // Use user_id from recipient_value as primary, fallback to recipient_id
          const userId = value?.user_id || (r as any).recipient_id;
          return JSON.stringify({
            id: userId,
            name: value?.name || 'User',
            email: '', // Email will be populated from teamUsers list when component loads
          });
        })
        // Remove duplicates based on user_id
        .filter((value, index, self) => {
          try {
            const parsed = JSON.parse(value);
            return (
              index ===
              self.findIndex(v => {
                try {
                  const vParsed = JSON.parse(v);
                  return vParsed.id === parsed.id;
                } catch {
                  return false;
                }
              })
            );
          } catch {
            return true;
          }
        });

      // Extract metric from the first threshold_config if available
      const metricFromThreshold = alert.brands?.[0]?.threshold_config?.[0]?.metric || undefined;

      // Map API communication method IDs to the correct numeric IDs
      const mapCommMethodId = (apiId: string, type: string): string => {
        // API returns IDs like 'slack', 'email', 'web', but we need '1', '2', '3', '4'
        const lowerApiId = apiId.toLowerCase();
        const lowerType = type.toLowerCase();

        if (lowerApiId === 'slack' || lowerType.includes('slack')) return '1';
        if (lowerApiId === 'email' || lowerType.includes('email')) return '2';
        if (lowerApiId === 'web' || lowerType.includes('brand')) return '3';
        if (lowerType.includes('user') && lowerType.includes('notification')) return '4';

        return apiId; // Fallback to original ID
      };

      const emailFromAlert =
        alert.ownerEmail ||
        alert.recipients?.users?.find(user => user.user_id === alert.ownerUserId)?.email ||
        alert.recipients?.email?.[0]?.recipient_value?.email ||
        userEmail ||
        '';

      setFormData({
        alertName: isDuplicating ? `${alert.name || ''} (Copy)` : alert.name || '',
        description: alert.description || '',
        emailId: emailFromAlert,
        toolName: alert.toolName,
        alertMetric: metricFromThreshold,
        alertTypeId: alert.alert_type_id,
        brandConfigs: [],
        communicationMethods: alert.communicationMethods
          .map(method => ({
            ...method,
            id: mapCommMethodId(method.id, method.type),
          }))
          .filter(method => userIsTeam || method.type !== 'Slack'),
        selectedMembers: alert.selectedMembers,
        messageTemplate: alert.alertMessage,
        alertMessage: alert.alertMessageForForm || '',
        notificationChannel: 'Slack',
        // Don't use backend cadence/scheduleConfig - let cron_info parser handle it
        cadence: 'realtime', // Will be overwritten by cron parser if cron_info exists
        scheduleConfig: {
          time: alert.schedule_config?.time || '09:00',
          timezone: alert.schedule_config?.timezone || 'America/New_York',
          days: [1, 2, 3, 4, 5],
          day: 1,
        },
        slackRecipients,
        emailRecipients,
        emailCcRecipients,
        liftRecipients,
        liftNotificationUserRecipients,
        messageTemplates: templates,
        cron_info: alert.cron_info,
      });

      // Prefill selected brands and thresholds if present
      if (alert.brands && alert.brands.length > 0) {
        const mapped: BrandConfig[] = alert.brands.map(b => ({
          brandId: b.brand_id,
          brandName: b.brand_name,
          thresholds:
            (b.threshold_config || []).map((t: any, idx: number) => {
              // Include all operators: standard comparison operators and pacing operators (+, -, +-)
              const allowedOps = [
                '<',
                '>',
                '<=',
                '>=',
                '==',
                '!=',
                '+',
                '-',
                '+-',
                '+=',
                '-=',
                '+-=',
              ] as const;
              const op = (allowedOps as readonly string[]).includes(t.operator)
                ? (t.operator as (typeof allowedOps)[number])
                : '<';

              // Extract logicalOperator if present (converted from connect_to_next by mapper)
              const logicalOp = (t as any).logicalOperator as 'AND' | 'OR' | 'NONE' | undefined;

              const mappedThreshold = {
                id: `${b.brand_id}-${idx}`,
                // EMQ fields - mapper outputs camelCase
                pixelId: (t as any).pixelId || undefined,
                pixelName: (t as any).pixelName || undefined,
                eventId: (t as any).eventId || undefined,
                eventName: (t as any).eventName || undefined,
                // Pacing fields
                dimension: (t as any).dimension || undefined,
                dimensionId: (t as any).dimensionId || undefined,
                dimensionType: (t as any).dimensionType || undefined,
                dimensionValue: (t as any).dimensionValue || undefined,
                dimensionCategory: (t as any).dimensionCategory || undefined,
                metric: (t as any).metric || undefined,
                metricsId: (t as any).metricsId || undefined,
                metricsCategory: (t as any).metricsCategory || undefined,
                startDate: (t as any).startDate || undefined,
                endDate: (t as any).endDate || undefined,
                calendarType: (t as any).calendarType || undefined,
                comparisonDate: (t as any).comparisonDate || undefined,
                pacingValueType: (t as any).pacingValueType || undefined,
                // Taxonomy fields
                taxonomyType: (t as any).taxonomyType || undefined,
                taxonomyValueType: (t as any).taxonomyValueType || undefined,
                updatedAtDate: (t as any).updatedAtDate || undefined,
                updatedAtTime: (t as any).updatedAtTime || undefined,
                // Common fields
                threshold: t.value || 0,
                operator: op,
                logicalOperator: logicalOp, // Add logical operator for UI
              };

              return mappedThreshold;
            }) || [],
        }));

        setSelectedBrands(mapped);
        // Also update brandConfigs in formData
        setFormData(prev => ({ ...prev, brandConfigs: mapped }));
      } else if (alert.recipients?.web && alert.recipients.web.length > 0) {
        // Fallback: prefill brands from web recipients without thresholds
        const mapped: BrandConfig[] = alert.recipients.web.map((w, idx) => ({
          brandId: w.brand_id || idx,
          brandName: w.brand_name || 'Brand',
          thresholds: [],
        }));
        setSelectedBrands(mapped);
      }
    }
  }, [alert, isDuplicating, userEmail, userIsTeam]);

  const handleInputChange = (field: keyof AlertFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return {
    formData,
    setFormData,
    selectedBrands,
    setSelectedBrands,
    handleInputChange,
  };
};